import { useState, useEffect, useCallback } from "react";
import { ContractorApi } from "api/contractors/service";
import { ContractorDashboard } from "./ContractorDashboard";
import { ContractorProfile } from "./ContractorProfile";
import { WorkOrderDetailView } from "./WorkOrderDetailView";
import { BASE_URL } from "utils/constant";
import RequireAccess from "components/RequireAccess";
// Mock data
const contractorsData = [
    {
        id: "1",
        name: "ABC Group",
        category: "Electrical",
        contact: "+91 98765 43210",
        address: "123 Industrial Area, Howrah",
        email: "abc@group.com",
        projects: 3,
        workOrders: 5,
        totalPaid: 180000,
        totalDue: 54000,
        totalWorkOrderValue: 234000,
        activeProjects: 2,
    },
    {
        id: "2",
        name: "XYZ Construction",
        category: "Civil",
        contact: "+91 87654 32109",
        address: "456 Main Road, Kolkata",
        email: "xyz@construction.com",
        projects: 5,
        workOrders: 8,
        totalPaid: 450000,
        totalDue: 120000,
        totalWorkOrderValue: 570000,
        activeProjects: 3,
    },
    {
        id: "3",
        name: "Prime Interiors",
        category: "Interior",
        contact: "+91 76543 21098",
        address: "789 Park Street, Kolkata",
        email: "prime@interiors.com",
        projects: 2,
        workOrders: 3,
        totalPaid: 95000,
        totalDue: 25000,
        totalWorkOrderValue: 120000,
        activeProjects: 1,
    },
    {
        id: "4",
        name: "Steel Works Ltd",
        category: "Structural",
        contact: "+91 65432 10987",
        address: "321 Industrial Belt, Durgapur",
        email: "steel@works.com",
        projects: 4,
        workOrders: 6,
        totalPaid: 320000,
        totalDue: 80000,
        totalWorkOrderValue: 400000,
        activeProjects: 2,
    },
    {
        id: "5",
        name: "Plumbing Experts",
        category: "Plumbing",
        contact: "+91 54321 09876",
        address: "654 Water Lane, Howrah",
        email: "plumbing@experts.com",
        projects: 6,
        workOrders: 10,
        totalPaid: 180000,
        totalDue: 45000,
        totalWorkOrderValue: 225000,
        activeProjects: 4,
    },
];

const mapPaymentStructureChoiceToString = (payment_structure) => {
    const paymentStructureMap = {
        'MILESTONE_BASED': 'Milestone',
        'MONTHLY_RUNNING_BILL': 'Monthly',
        'LUMPSUM_CUSTOM': 'Lumpsum',
    };

    return paymentStructureMap[payment_structure] || payment_structure;
};

const mapContractTypeChoiceToString = (contract_type) => {
    const contractTypeMap = {
        'LABOUR': 'Labour',
        'ITEM_RATE': 'Item Rate',
        'LUMPSUM': 'Lumpsum',
    };

    return contractTypeMap[contract_type] || contract_type;
};

const mapPaymentModeChoiceToString = (payment_mode) => {
    const paymentModeMap = {
        'CASH': 'Cash',
        'CHEQUE': 'Cheque',
        'ONLINE': 'Online',
        'BANK_TRANSFER': 'Bank Transfer',
    };
    return paymentModeMap[payment_mode] || payment_mode;
};

const formatContractorsData = (data) => {
    return data.map((contractor) => ({
        ...contractor,
        category: contractor.work_name,
        projects: contractor.projects_associated,
        workOrders: contractor.workorders_count,
        totalPaid: contractor.total_paid,
        totalDue: contractor.due_payment,
        totalWorkOrderValue: contractor.due_payment + contractor.total_paid,
        activeProjects: contractor.projects_associated
    }));
};

const formatWorkOrdersData = (data, contractorId) => {
    return data.map((wo) => ({
        ...wo,
        id: wo.workorder_no,
        workorder_id: wo.workorder_id,
        contractorId: contractorId,
        projectName: wo.site,
        startDate: wo.start_date,
        endDate: wo.end_date,
        contractType: mapContractTypeChoiceToString(wo.contract_type),
        paymentStructure: mapPaymentStructureChoiceToString(wo.payment_structure),
        value: wo.value,
        status: "Active",
        paidAmount: wo.total_paid,
        milestones: [],
    }));
};

const formatPaymentsData = (data) => {
    if (!data?.payments) return [];
    return data.payments.map((p) => ({
        id: `P${p.payment_id}`,
        date: p.date,
        workOrderId: p.work_order_no,
        projectName: p.site_name,
        type: p.type,
        mode: mapPaymentModeChoiceToString(p.payment_mode),
        amount: p.amount,
        reference: p.reference_no,
        status: "Success",
        remarks: p.remarks,
        created_by: p.created_by,
        attachment_file : p.attachment_file,
    }));
};

// Mock data removed as APIs are integrated

const ContractorManagement = ({ currentMember }) => {
    const [selectedContractor, setSelectedContractor] = useState(null);
    const [selectedContractorId, setSelectedContractorId] = useState(() => {
        try {
            return localStorage.getItem("selected_contractor_id") || null;
        } catch (_) {
            return null;
        }
    });
    const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
    const [contractors, setContractors] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [payments, setPayments] = useState([]);
    const [highlightedWorkOrder, setHighlightedWorkOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const organization = JSON.parse(localStorage.getItem("organization")) || {};

    const fetchContractors = useCallback(async () => {
        if (!organization?.id) return;

        try {
            setIsLoading(true);
            const response = await ContractorApi.fetchContractors({
                organization_id: organization.id
            });
            console.log("response", response);
            if (response.status) {
                setContractors(formatContractorsData(response.data));
            } else {
                setError(response.message || "Failed to fetch contractors");
            }
        } catch (err) {
            console.error("Error fetching contractors:", err);
            setError("An error occurred while fetching contractors");
        } finally {
            setIsLoading(false);
        }
    }, [organization.id]);

    useEffect(() => {
        fetchContractors();
    }, [fetchContractors]);

    // Restore selected contractor after refresh (once contractors list is loaded)
    useEffect(() => {
        if (!selectedContractorId) return;
        if (selectedContractor) return;
        if (!Array.isArray(contractors) || contractors.length === 0) return;
        const found = contractors.find((c) => String(c.id) === String(selectedContractorId));
        if (found) setSelectedContractor(found);
    }, [contractors, selectedContractorId, selectedContractor]);

    const fetchContractorDetails = useCallback(async (contractorId) => {
        try {
            const [woResponse, paymentsResponse] = await Promise.all([
                ContractorApi.getWorkOrdersByContractor(contractorId),
                ContractorApi.getPaymentsByContractor(contractorId)
            ]);

            if (woResponse.status) {
                setWorkOrders(formatWorkOrdersData(woResponse.data, contractorId));
            }
            if (paymentsResponse.status) {
                setPayments(formatPaymentsData(paymentsResponse.data));
            }
        } catch (err) {
            console.error("Error fetching contractor details:", err);
        }
    }, []);

    useEffect(() => {
        if (selectedContractor) {
            fetchContractorDetails(selectedContractor.id);
        }
    }, [selectedContractor, fetchContractorDetails]);

    const handleSelectContractor = (contractor) => {
        setSelectedContractor(contractor);
        const id = contractor?.id != null ? String(contractor.id) : null;
        setSelectedContractorId(id);
        try {
            if (id) localStorage.setItem("selected_contractor_id", id);
            else localStorage.removeItem("selected_contractor_id");
        } catch (_) {
            // ignore
        }
    };

    const handleBackToDashboard = () => {
        setSelectedContractor(null);
        setSelectedContractorId(null);
        try {
            localStorage.removeItem("selected_contractor_id");
        } catch (_) {
            // ignore
        }
        setSelectedWorkOrder(null);
    };

    const handleViewWorkOrder = (workOrder) => {
        setSelectedWorkOrder(workOrder);
    };

    const handleBackToProfile = () => {
        setSelectedWorkOrder(null);
    };

    const handleUpgradeContract = () => {
        // This would open the create work order dialog with pre-filled values
        console.log("Upgrade contract clicked");
    };

    const handleAddContractor = (newContractor) => {
        const contractor = {
            ...newContractor,
            id: `${Date.now()}`,
            projects: 0,
            workOrders: 0,
            totalPaid: 0,
            totalDue: 0,
            totalWorkOrderValue: 0,
            activeProjects: 0,
        };
        // Prefer server id if provided by API
        const id = newContractor?.id != null ? String(newContractor.id) : contractor.id;
        const nextContractor = { ...contractor, id };
        setContractors([...contractors, nextContractor]);
        // Immediately select newly created contractor so profile has correct id
        handleSelectContractor(nextContractor);
    };

    const handleContractorUpdated = (patch) => {
        if (!selectedContractor) return;
        const nextSelected = { ...selectedContractor, ...patch };
        setSelectedContractor(nextSelected);
        setContractors((prev) =>
            (Array.isArray(prev) ? prev : []).map((c) =>
                String(c.id) === String(nextSelected.id) ? { ...c, ...patch } : c
            )
        );
        fetchContractors();
    };

    const handleContractorDeleted = (deletedId) => {
        setContractors((prev) =>
            (Array.isArray(prev) ? prev : []).filter((c) => String(c.id) !== String(deletedId))
        );
        if (selectedContractor && String(selectedContractor.id) === String(deletedId)) {
            handleBackToDashboard();
        }
        fetchContractors();
    };

    const handleAddPayment = (contractor_id) => {
        // const newPayment = {
        //     ...payment,
        //     id: `P${Date.now()}`,
        // };
        // setPayments([...payments, newPayment]);

        // // Update work order paid amount
        // setWorkOrders(workOrders.map(wo => {
        //     if (wo.id === payment.workOrderId) {
        //         return { ...wo, paidAmount: wo.paidAmount + payment.amount };
        //     }
        //     return wo;
        // }));

        // // Update contractor totals
        // if (selectedContractor) {
        //     setContractors(contractors.map(c => {
        //         if (c.id === selectedContractor.id) {
        //             return {
        //                 ...c,
        //                 totalPaid: c.totalPaid + payment.amount,
        //                 totalDue: c.totalDue - payment.amount,
        //             };
        //         }
        //         return c;
        //     }));
        //     setSelectedContractor({
        //         ...selectedContractor,
        //         totalPaid: selectedContractor.totalPaid + payment.amount,
        //         totalDue: selectedContractor.totalDue - payment.amount,
        //     });
        // }
        fetchContractorDetails(contractor_id);
    };

    const handleCreateWorkOrder = (contractor_id) => {
        // const newId = `WO_${Date.now().toString().slice(-4)}`;
        // const newWorkOrder = {
        //     ...workOrder,
        //     id: newId,
        //     paidAmount: 0,
        // };
        // setWorkOrders([...workOrders, newWorkOrder]);
        // setHighlightedWorkOrder(newId);
        // setTimeout(() => setHighlightedWorkOrder(null), 5000);

        // // Update contractor stats
        // setContractors(contractors.map(c => {
        //     if (c.id === workOrder.contractorId) {
        //         return {
        //             ...c,
        //             workOrders: c.workOrders + 1,
        //             totalWorkOrderValue: c.totalWorkOrderValue + workOrder.value,
        //             totalDue: c.totalDue + workOrder.value,
        //         };
        //     }
        //     return c;
        // }));
        fetchContractorDetails(contractor_id);
    };

    const contractorWorkOrders = selectedContractor
        ? workOrders.filter(wo => wo.contractorId === selectedContractor.id)
        : [];

    const contractorPayments = selectedContractor
        ? payments.filter(p => contractorWorkOrders.some(wo => wo.id === p.workOrderId))
        : [];


    if (currentMember?.contractor_control === false && currentMember?.joined_as !== "owner" && currentMember?.is_admin === false) {
        return (
            <RequireAccess
                module="Contractor"
                currentMember={currentMember}
                permission="contractor_control"
            />
        );
    }
    // Show Work Order Detail View
    if (selectedWorkOrder && selectedContractor) {
        return (
            <div className="h-full p-3 overflow-auto w-[85%]">
                <WorkOrderDetailView
                    workOrder={selectedWorkOrder}
                    contractor={selectedContractor}
                    onBack={handleBackToProfile}
                    onUpgrade={handleUpgradeContract}
                />
            </div>
        );
    }

    if (selectedContractor) {
        return (
            <ContractorProfile
                contractor={selectedContractor}
                workOrders={contractorWorkOrders}
                payments={contractorPayments}
                contractors={contractors}
                onBack={handleBackToDashboard}
                onAddPayment={handleAddPayment}
                onCreateWorkOrder={handleCreateWorkOrder}
                onViewWorkOrder={handleViewWorkOrder}
                onUpdated={handleContractorUpdated}
                onDeleted={handleContractorDeleted}
            />
        );
    }

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] w-[80%] mx-3 bg-background mt-4 rounded-xl border border-border overflow-hidden p-6 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    // if (error) {
    //     return (
    //         <div className="flex flex-col items-center justify-center min-h-[400px] w-[80%] mx-3 bg-background mt-4 rounded-xl border border-border overflow-hidden p-6 text-center">
    //             <p className="text-destructive font-semibold text-lg mb-2">Error</p>
    //             <p className="text-sm-secondary-foreground mb-4">{error}</p>
    //             <button
    //                 onClick={fetchContractors}
    //                 className="px-4 py-2 bg-sm-primary text-sm-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
    //             >
    //                 Try Again
    //             </button>
    //         </div>
    //     );
    // }

    return (
        <ContractorDashboard
            contractors={contractors}
            onSelectContractor={handleSelectContractor}
            onAddContractor={handleAddContractor}
            onCreateWorkOrder={handleCreateWorkOrder}
        />
    );
};

export default ContractorManagement;
